import { array, object, string, TestContext } from 'yup';

import { convertHoursToMilliseconds, convertMinutesToMilliseconds } from '@utils/date';
import { getSchema } from '@utils/schemaUtils';

import { TValueOfStringRecord } from '@interfaces/common/genericWithModifying';

import { EConditionFields, TConditionFields } from './enums';

import { mandatoryErrMessage } from '@constants/validationConstants';

export const EValidationErrorsCondition = {
	checkEvery: 'The value in ‘Check every’ must be less than or equal to the value in ‘Evaluation window’',
	checkEveryTime: 'The value in ‘Check every’ must be between 1 minute and 23h 59 min',
	evaluationWindow: 'The value in ‘Evaluation window’ must be greater than or equal to the value in ‘Check every’',
	evaluationWindowTime: 'The value in ‘Evaluation window’ must be between 1 minute and 23h 59 min',
} as const;

export type TValidationErrorsCondition = TValueOfStringRecord<typeof EValidationErrorsCondition>

export const conditionFields: Record<TConditionFields, any> = {
	[EConditionFields.thresholdSource]: string().required(mandatoryErrMessage('Threshold Source')),
	[EConditionFields.thresholds]: array().nullable().required(mandatoryErrMessage('Thresholds')),
	[EConditionFields.applyFor]: string().required(mandatoryErrMessage('Apply For')),
	[EConditionFields.checkEveryHours]: object()
		.nullable()
		.test('checkEveryHours', EValidationErrorsCondition.checkEveryTime, (value, context) => {
			const { checkEveryMinutes } = context.parent;

			return !(checkEveryMinutes?.value === 0 && value?.value === 0);
		})
		.test('checkEveryHours', EValidationErrorsCondition.checkEvery, testCheckEveryHours)
		.required(mandatoryErrMessage('Check Every')),
	[EConditionFields.checkEveryMinutes]: object()
		.nullable()
		.required(mandatoryErrMessage('Check Every'))
		.test('checkEveryMinutes', EValidationErrorsCondition.checkEveryTime, (value, context) => {
			const { checkEveryHours } = context.parent;

			return !(checkEveryHours?.value === 0 && value?.value === 0);
		})
		.test('checkEveryMinutes', EValidationErrorsCondition.checkEvery, testCheckEveryMinutes),
	[EConditionFields.evaluationWindowHours]: object()
		.nullable()
		.required(mandatoryErrMessage('Evaluation Window'))
		.test('evaluationWindowHours', EValidationErrorsCondition.evaluationWindowTime, (value, context) => {
			const { evaluationWindowMinutes } = context.parent;

			return !(evaluationWindowMinutes?.value === 0 && value?.value === 0);
		})
		.test('evaluationWindowHours', EValidationErrorsCondition.evaluationWindow, testEvaluationWindowHours),
	[EConditionFields.evaluationWindowMinutes]: object()
		.nullable()
		.required(mandatoryErrMessage('Evaluation Window'))
		.test('evaluationWindowMinutes', EValidationErrorsCondition.evaluationWindowTime, (value, context) => {
			const { evaluationWindowHours } = context.parent;

			return !(evaluationWindowHours?.value === 0 && value?.value === 0);
		})
		.test('evaluationWindowMinutes', EValidationErrorsCondition.evaluationWindow, testEvaluationWindowMinutes),
};
export const conditionSchema = getSchema(conditionFields);

function testCheckEveryHours(value: any, context: TestContext<any>): boolean {
	const { evaluationWindowHours, evaluationWindowMinutes, checkEveryMinutes } = context.parent;

	if (!evaluationWindowHours || !evaluationWindowMinutes || !checkEveryMinutes) return true;

	const evaluationWindowMilliseconds: number = convertHoursToMilliseconds(evaluationWindowHours?.value || 0)
		+ convertMinutesToMilliseconds(evaluationWindowMinutes?.value || 0);
	const checkEveryMilliseconds: number = convertHoursToMilliseconds(value?.value || 0)
		+ convertMinutesToMilliseconds(checkEveryMinutes?.value || 0);

	if (checkEveryMilliseconds > evaluationWindowMilliseconds) {
		context.createError({ message: 'The value in ‘Check every’ must be less than or equal to the value in ‘Evaluation window’' });

		return false;
	}

	return true;
}

function testCheckEveryMinutes(value: any, context: TestContext<any>): boolean {
	const { evaluationWindowHours, evaluationWindowMinutes, checkEveryHours } = context.parent;

	if (!evaluationWindowHours || !evaluationWindowMinutes || !checkEveryHours) return true;

	const evaluationWindowMilliseconds: number = convertHoursToMilliseconds(evaluationWindowHours?.value || 0)
		+ convertMinutesToMilliseconds(evaluationWindowMinutes?.value || 0);
	const checkEveryMilliseconds: number = convertMinutesToMilliseconds(value?.value || 0)
		+ convertHoursToMilliseconds(checkEveryHours?.value || 0);

	if (checkEveryMilliseconds > evaluationWindowMilliseconds) {
		context.createError({ message: 'The value in ‘Check every’ must be less than or equal to the value in ‘Evaluation window’' });

		return false;
	}

	return true;
}

function testEvaluationWindowHours(value: any, context: TestContext<any>): boolean {
	const { checkEveryHours, checkEveryMinutes, evaluationWindowMinutes } = context.parent;

	if (!checkEveryHours || !checkEveryMinutes || !evaluationWindowMinutes) return true;

	const evaluationWindowMilliseconds: number = convertHoursToMilliseconds(value?.value || 0)
		+ convertMinutesToMilliseconds(evaluationWindowMinutes?.value || 0);
	const checkEveryMilliseconds: number = convertHoursToMilliseconds(checkEveryHours?.value || 0)
		+ convertMinutesToMilliseconds(checkEveryMinutes?.value || 0);

	if (evaluationWindowMilliseconds < checkEveryMilliseconds) {
		context.createError({ message: 'Evaluation Window must be less or equal then Check Every.' });

		return false;
	}

	return true;
}

function testEvaluationWindowMinutes(value: any, context: TestContext<any>): boolean {
	const { checkEveryHours, checkEveryMinutes, evaluationWindowHours } = context.parent;

	if (!checkEveryHours || !checkEveryMinutes || !evaluationWindowHours) return true;

	const evaluationWindowMilliseconds: number = convertMinutesToMilliseconds(value?.value || 0)
		+ convertHoursToMilliseconds(evaluationWindowHours?.value || 0);
	const checkEveryMilliseconds: number = convertHoursToMilliseconds(checkEveryHours?.value || 0)
		+ convertMinutesToMilliseconds(checkEveryMinutes?.value || 0);

	if (evaluationWindowMilliseconds < checkEveryMilliseconds) {
		context.createError({ message: 'Evaluation Window must be less or equal then Check Every.' });

		return false;
	}

	return true;
}
